/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Modal } from 'antd';

import { useMessage } from '@/hooks/message';
import { useDeleteOAuthClientMutation } from '@/hooks/mutation/useDeleteOAuthClientMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

type DeleteOAuthClientModalProps = {
  oauthClientId: string | undefined;
  oauthClientName: string | undefined;
  onCancelDeletion: () => void;
  onDeleteSuccess?: () => void;
};

export function DeleteOAuthClientModal({
  oauthClientId,
  oauthClientName,
  onCancelDeletion,
  onDeleteSuccess,
}: DeleteOAuthClientModalProps) {
  const { t } = useMimicTranslation('oauthClients');

  const [message] = useMessage();

  const { mutate: deleteOAuthClient, isPending } = useDeleteOAuthClientMutation({
    onError: () => {
      message.error(t('deleteError'));
    },
    onSuccess: () => {
      message.success(t('clientDeleted'));
      closeDeleteDialog();
      onDeleteSuccess?.();
    },
  });

  const onOAuthClientDelete = () => {
    if (oauthClientId) {
      deleteOAuthClient(oauthClientId);
    }
  };

  const closeDeleteDialog = () => {
    onCancelDeletion();
  };

  return (
    <Modal
      open={Boolean(oauthClientId)}
      onCancel={onCancelDeletion}
      onOk={onOAuthClientDelete}
      cancelText={t('confirmDelete.cancel')}
      cancelButtonProps={{ disabled: isPending }}
      okText={t('confirmDelete.ok')}
      okButtonProps={{ danger: true, loading: isPending }}
    >
      <Container padding="sm">
        <Title level={4}>{t('confirmDelete.title', { name: oauthClientName })}</Title>
        <Text>{t('confirmDelete.body1')}</Text>
        <Text>{t('confirmDelete.body2')}</Text>
      </Container>
    </Modal>
  );
}
